// Opt-in browser error reporting (errors only).
//
// Off unless REACT_APP_SENTRY_DSN is set at build time: without a DSN nothing
// is initialised and no network request to Sentry is ever made. The DSN of a
// browser project is public by design. No session replay, no tracing, no
// default PII. This app handles wallets, so every event is scrubbed before it
// is sent (wallet addresses, keys, signatures, tokens, query strings).
import * as Sentry from "@sentry/react";

const REDACTED = "[redacted]";

const SENSITIVE_KEY_RE =
  /(private|secret|token|passw|mnemonic|seed|signature|sig$|wallet|address|authorization|auth(?!or)|cookie|api[_-]?key|credential|dsn|session|jwt|bearer|keypair|signer)/i;

const VALUE_PATTERNS = [
  /0x[a-fA-F0-9]{40,}/g, // EVM address / private key / tx hash
  /\b[a-fA-F0-9]{64}\b/g, // raw 32-byte hex
  /\beyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]*/g, // JWT
  /\bbearer\s+[A-Za-z0-9._~+/=-]{8,}/gi,
  /\b[1-9A-HJ-NP-Za-km-z]{32,90}\b/g, // base58: Solana address / key / signature
  /[A-Za-z0-9+/]{43,}={0,2}/g, // long base64 blobs
];

const MAX_DEPTH = 12;

function redactString(text) {
  return VALUE_PATTERNS.reduce((acc, re) => acc.replace(re, REDACTED), text);
}

function scrub(value, depth = 0) {
  if (depth > MAX_DEPTH) return REDACTED;
  if (typeof value === "string") return redactString(value);
  if (Array.isArray(value)) return value.map((v) => scrub(v, depth + 1));
  if (value && typeof value === "object") {
    const out = {};
    for (const [key, item] of Object.entries(value)) {
      out[key] = SENSITIVE_KEY_RE.test(key) ? REDACTED : scrub(item, depth + 1);
    }
    return out;
  }
  return value;
}

function stripQuery(url) {
  return typeof url === "string" ? url.split("?", 1)[0].split("#", 1)[0] : url;
}

export function scrubBreadcrumb(crumb) {
  // Console output and DOM input can carry anything the user typed or the app logged.
  if (crumb?.category === "console" || String(crumb?.category || "").startsWith("ui.input")) {
    return null;
  }
  const cleaned = scrub(crumb);
  if (cleaned.data && typeof cleaned.data === "object") {
    if ("url" in cleaned.data) cleaned.data.url = stripQuery(cleaned.data.url);
    if ("to" in cleaned.data) cleaned.data.to = stripQuery(cleaned.data.to);
    if ("from" in cleaned.data) cleaned.data.from = stripQuery(cleaned.data.from);
    delete cleaned.data["http.query"];
    delete cleaned.data["http.fragment"];
  }
  return cleaned;
}

export function scrubEvent(event) {
  try {
    const cleaned = scrub(event);
    delete cleaned.user;
    if (cleaned.request && typeof cleaned.request === "object") {
      const { url } = cleaned.request;
      cleaned.request = url ? { url: stripQuery(url) } : {};
    }
    if (cleaned.breadcrumbs) {
      cleaned.breadcrumbs = (cleaned.breadcrumbs || [])
        .map((crumb) => scrubBreadcrumb(crumb))
        .filter(Boolean);
    }
    return cleaned;
  } catch {
    return null; // fail closed: an event we cannot scrub is not sent
  }
}

export function buildSentryOptions(env = process.env) {
  const dsn = (env.REACT_APP_SENTRY_DSN || "").trim();
  if (!dsn) return null;
  const release = (env.REACT_APP_RELEASE || "").trim();
  return {
    dsn,
    environment: (env.REACT_APP_SENTRY_ENVIRONMENT || "").trim() || env.NODE_ENV || "production",
    ...(release ? { release } : {}),
    sendDefaultPii: false,
    // Sentry JS v11 replaced sendDefaultPii by dataCollection: everything off.
    dataCollection: {
      userInfo: false,
      cookies: false,
      httpHeaders: false,
      httpBodies: [],
      urlQueryParams: false,
      stackFrameVariables: false,
    },
    tracesSampleRate: 0,
    // No replay: neither replaysSessionSampleRate nor the replay integration.
    beforeSend: scrubEvent,
    beforeBreadcrumb: scrubBreadcrumb,
  };
}

export function initSentry(env = process.env) {
  const options = buildSentryOptions(env);
  if (!options) return false;
  try {
    Sentry.init(options);
  } catch {
    return false; // monitoring must never stop the app from starting
  }
  return true;
}

// React 19 root error hooks; plain no-ops when Sentry is not initialised.
export const reactRootErrorHandlers = () => ({
  onUncaughtError: Sentry.reactErrorHandler(),
  onCaughtError: Sentry.reactErrorHandler(),
});
